import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFeedback,
  fetchFeedbacksPaged,
  updateFeedbackStatus,
  type Feedback,
  type PagedData,
} from '../../lib/api';
import { type FeedbackQueryParams, queryKeys } from './queryKeys';

type FeedbacksInfiniteParams = Omit<FeedbackQueryParams, 'page' | 'pageSize'>;

export const useFeedbacksInfinite = (
  token: string | null,
  params: FeedbacksInfiniteParams = {},
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery<
    PagedData<Feedback>,
    Error,
    PagedData<Feedback>,
    ReturnType<typeof queryKeys.feedbacks.infinite>,
    number
  >({
    queryKey: queryKeys.feedbacks.infinite(params),
    queryFn: ({ pageParam }) => fetchFeedbacksPaged(token as string, { ...params, page: pageParam, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(token) && (options?.enabled ?? true),
    select: (data) =>
      ({
        pages: data.pages,
        pageParams: data.pageParams,
        data: data.pages.flatMap((page) => page.data),
        meta: data.pages[data.pages.length - 1]?.meta,
      }) as unknown as PagedData<Feedback>,
  });
};

type CreateFeedbackInput = {
  category: string;
  title: string;
  content: string;
};

export const useCreateFeedbackMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateFeedbackInput) => createFeedback(token as string, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedbacks.all });
    },
  });
};

export const useUpdateFeedbackStatusMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; status: Feedback['status'] }) =>
      updateFeedbackStatus(token as string, input.id, { status: input.status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.feedbacks.all });

      const previousQueries = queryClient.getQueriesData<
        PagedData<Feedback> | { pages: Array<PagedData<Feedback>>; pageParams: unknown[] }
      >({ queryKey: queryKeys.feedbacks.all });

      queryClient.setQueriesData<PagedData<Feedback> | { pages: Array<PagedData<Feedback>>; pageParams: unknown[] }>(
        { queryKey: queryKeys.feedbacks.all },
        (old) => {
          if (!old) return old;
          if ('pages' in old) {
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: page.data.map((f) => (f.id === id ? { ...f, status } : f)),
              })),
            };
          }
          if ('data' in old && Array.isArray(old.data)) {
            return { ...old, data: old.data.map((f) => (f.id === id ? { ...f, status } : f)) };
          }
          return old;
        },
      );

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedbacks.all });
    },
  });
};
